'use client';

import { useId, useState } from 'react';
import type { FocusEventHandler, InputHTMLAttributes, ReactNode, Ref } from 'react';
import { cn } from 'utils/styles';

interface CalculatorInputProps extends InputHTMLAttributes<HTMLInputElement> {
  ref?: Ref<HTMLInputElement>;
  label: string;
  hint?: ReactNode;
  error?: ReactNode;
  invalid?: boolean;
}

export const CalculatorInput = ({
  ref,
  id,
  label,
  hint,
  error,
  invalid = false,
  className,
  placeholder = '$0.00',
  onFocus,
  onBlur,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  ...inputProps
}: CalculatorInputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const hasError = invalid && Boolean(error);
  const describedBy =
    [ariaDescribedBy, hasError ? errorId : undefined, hint ? hintId : undefined]
      .filter(value => value !== undefined)
      .join(' ') || undefined;
  const handleFocus: FocusEventHandler<HTMLInputElement> = event => {
    setIsFocused(true);
    onFocus?.(event);
  };
  const handleBlur: FocusEventHandler<HTMLInputElement> = event => {
    setIsFocused(false);
    onBlur?.(event);
  };

  return (
    <div className="w-full">
      <label htmlFor={inputId} className="block text-body-saans">
        {label}
      </label>
      <div className="relative mt-3">
        <input
          {...inputProps}
          ref={ref}
          id={inputId}
          placeholder={placeholder}
          aria-describedby={describedBy}
          aria-invalid={invalid || ariaInvalid || undefined}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={cn(
            'h-16 w-full rounded-lg border border-border-primary bg-bg-primary px-6 text-body-saans text-content-primary outline-none transition-shadow',
            'placeholder:text-content-secondary hover:ring-[6px] hover:ring-bg-tertiary focus:border-content-primary focus:pl-10 focus:placeholder:text-transparent focus:ring-[6px] focus:ring-bg-tertiary',
            'disabled:cursor-not-allowed disabled:bg-accent-grey-100 disabled:text-content-disabled-primary disabled:hover:ring-transparent',
            invalid &&
              'border-util-alert-500 bg-util-alert-100 hover:ring-util-alert-200 focus:border-util-alert-500 focus:ring-util-alert-200',
            className,
          )}
        />
        {isFocused && (
          <span aria-hidden className="pointer-events-none absolute top-1/2 left-6 -translate-y-1/2 text-body-saans">
            $
          </span>
        )}
      </div>
      {hasError && (
        <p id={errorId} role="alert" aria-live="assertive" className="mt-3 text-body-saans text-util-alert-500">
          {error}
        </p>
      )}
      {hint && (
        <p id={hintId} className="mt-2 text-body-xs text-content-secondary">
          {hint}
        </p>
      )}
    </div>
  );
};
